'use client';

import clsx from 'clsx';
import { FC } from 'react';

import { CarComparisonAdd } from 'src/car-comparison/components/CarComparisonAdd/CarComparisonAdd';
import { Link } from 'src/general/components/Link/Link';
import { ProductImage } from 'src/general/components/ProductImage/ProductImage';
import { Shade } from 'src/general/components/Shade/Shade';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { PlpBaseCarData } from 'src/types/Car.types';

import { AddToWatchListButton } from '../AddToWatchList/AddToWatchListButton';
import { ProductCardRepaymentsTooltip } from '../ProductCard/ProductCardRepaymentsTooltip';
import { StatusPill } from '../StatusPill/StatusPill';

type Props = {
  carData: PlpBaseCarData;
  weeklyRepayment: number;
  defaultRate: number;
  imagePriority?: boolean;
  onWatchlistStatusChange?: () => Promise<void>;
  imageTestId?: string;
};

export const PlpProductCard: FC<Props> = ({
  carData,
  weeklyRepayment,
  defaultRate,
  imagePriority,
  onWatchlistStatusChange,
  imageTestId,
}) => {
  const priceString = formatNumber(carData.price, { prepend: '$' });
  const dutyString = formatNumber(carData.dutyPrice, { prepend: '$' });
  const weeklyRepaymentString = formatNumber(weeklyRepayment, { prepend: '$' });
  const defaultRateString = formatNumber(defaultRate * 100, { decimals: 2, trimZeroDecimals: true, append: '%' });

  return (
    <div className={'PlpProductCard'} data-testid={`ProductCard-container-${carData.sku}`}>
      <Link
        href={`/product-detail/${carData.sku}`}
        className={clsx('PlpProductCard-link', 'Card-link', 'tileLink', 'c-shade_container', 'c-shade_container--grey')}
        data-testid="used-car-product-card"
      >
        <Shade />
        <span className="sr-only">{carData.model}</span>
      </Link>
      <ProductImage
        key={carData.sku}
        src={carData.imageUrl}
        alt={carData.model}
        className="PlpProductCard-image hidden sm:block"
        imagePriority={imagePriority}
        data-testid={imageTestId}
      />
      <Link
        href={`/product-detail/${carData.sku}`}
        className={clsx('PlpProductCard-imageWrapper', 'Card-link', 'flex sm:hidden')}
      >
        {carData.cardImages.map((image) => (
          <ProductImage
            key={image}
            src={image}
            alt={carData.model}
            className="PlpProductCard-image"
            imagePriority={imagePriority}
            data-testid={imageTestId}
          />
        ))}
      </Link>
      <p
        className={clsx('PlpProductCard-model', 'c-fs-body1', 'font-extrabold')}
        data-testid="ProductCard-component-titleText"
      >
        {carData.model}
      </p>
      <p
        className={clsx('PlpProductCard-variant', 'c-fs-body2', 'hidden sm:block')}
        data-testid="ProductCard-component-variantText"
      >
        {carData.variant}
      </p>
      <p className={clsx('PlpProductCard-details', 'c-fs-body2', 'hidden sm:block')}>
        <span data-testid="ProductCard-component-distanceText">
          {formatNumber(carData.kilometers, { append: ' km' })}
        </span>
        <span>&ensp;•&ensp;</span>
        <span data-testid="ProductCard-component-transmissionText">{carData.transmission}</span>
      </p>
      <div className={clsx('PlpProductCard-detailsWrapper', 'inline sm:hidden')}>
        <p
          className={clsx('PlpProductCard-variant', 'PlpProductCard-caption', 'inline sm:hidden')}
          data-testid="ProductCard-component-variantText"
        >
          {carData.variant}
          <span>&ensp;•&ensp;</span>
        </p>
        <p className={clsx('PlpProductCard-details', 'PlpProductCard-caption', 'inline sm:hidden')}>
          <span data-testid="ProductCard-component-distanceText">
            {formatNumber(carData.kilometers, { append: ' km' })}
          </span>
          <span>&ensp;•&ensp;</span>
          <span data-testid="ProductCard-component-transmissionText">{carData.transmission}</span>
        </p>
      </div>
      <div className="PlpProductCard-statusPillWrapper">
        <StatusPill sku={carData.sku} state={carData.state} />
      </div>
      <footer className="PlpProductCard-footer">
        <div className="PlpProductCard-footerContent">
          <p className={clsx('PlpProductCard-price', 'font-extrabold')} data-testid="ProductCard-component-priceText">
            {priceString}
          </p>
          <div
            className={clsx('PlpProductCard-repayment', 'c-fs-caption2')}
            data-testid="ProductCard-component-repaymentText"
          >
            <p>
              est. <strong>{weeklyRepaymentString}/wk</strong> based on
            </p>
            <div className="PlpProductCard-repaymentRate">
              <strong>{`${defaultRateString} p.a.*`} </strong>
              <ProductCardRepaymentsTooltip weeklyRepayment={weeklyRepaymentString} defaultRate={defaultRateString} />
            </div>
          </div>
        </div>
        <aside className="PlpProductCard-footerActions">
          <CarComparisonAdd sku={carData.sku} className="PlpProductCard-footerActionButton" />
          <AddToWatchListButton
            key={carData.sku}
            className="PlpProductCard-footerActionButton"
            sku={carData.sku}
            product={{
              ...carData,
              title: carData.model,
              price: priceString,
              dutyPrice: dutyString,
            }}
            onWatchlistStatusChange={onWatchlistStatusChange}
            buttonProps={{
              variant: 'secondary',
              fullWidth: true,
            }}
          />
        </aside>
      </footer>
    </div>
  );
};
